
import React, { useState, useEffect, useMemo } from "react";
import Pagelayout from "../../components/Pagelayout";
import {
  TextField,
  Autocomplete,
  Grid,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  createOrUpdatePayTemplateAPI,
  fetchPayTemplate,
  listPayHeadApi,
} from "../../parollAPIs";
import { useLocation, useNavigate } from "react-router-dom";
import { ACTION_TYPE, VIEW_TYPE } from "../../payrollEnums";
import { useSelector } from "react-redux";

const CreatePayTemplate = () => {
  const { singleViewData } = useSelector((state) => state.payrollSlice);
  const navigate = useNavigate();
  const location = useLocation();
  const singleViewId = location?.state?._id || null;

  const [payHeads, setPayHeads] = useState([]);
  const [templateData, setTemplateData] = useState({
    name: "",
    period: "MONTHLY",
    totalAmount: "",
    basic: null,
    allowances: null,
    deductions: null,
    otherAllowance: 0,
  });

  const [errors, setErrors] = useState({
    name: "",
    period: "",
    totalAmount: "",
    basic: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isEdit, setIsEdit] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payHeadOptions, setPayHeadOptions] = useState({
    basic: [],
    allowances: [],
    deductions: [],
  });

  const calculateAmount = (
    calculationType,
    amount,
    totalAmount,
    basicMonthly = null
  ) => {
    if (!calculationType || !amount || !totalAmount) {
      return { monthly: "0", yearly: "0" };
    }

    const totalAmountNum = parseFloat(totalAmount) || 0;
    const amountNum = parseFloat(amount) || 0;
    const isMonthly = templateData.period === "MONTHLY";

    switch (calculationType) {
      case "FIXED":
        return {
          monthly: isMonthly ? amountNum : (amountNum / 12).toFixed(2),
          yearly: isMonthly
            ? (amountNum * 12).toFixed(2)
            : amountNum.toFixed(2),
        };

      case "CTCPERCENT":
        const ctcAmount = (totalAmountNum * amountNum) / 100;
        return {
          monthly: isMonthly
            ? ctcAmount.toFixed(2)
            : (ctcAmount / 12).toFixed(2),
          yearly: isMonthly
            ? (ctcAmount * 12).toFixed(2)
            : ctcAmount.toFixed(2),
        };

      case "BASICPERCENT":
        if (!basicMonthly) return { monthly: "0", yearly: "0" };

        // First get the base monthly amount from basic salary
        const baseMonthly = isMonthly
          ? parseFloat(basicMonthly)
          : parseFloat(basicMonthly) * 12;

        // Calculate percentage of basic
        const basicPercentAmount = (baseMonthly * amountNum) / 100;

        // Adjust for period
        const basicMonthlyAmount = isMonthly
          ? basicPercentAmount
          : basicPercentAmount / 12;
        const basicYearlyAmount = isMonthly
          ? basicPercentAmount * 12
          : basicPercentAmount;

        return {
          monthly: basicMonthlyAmount.toFixed(2),
          yearly: basicYearlyAmount.toFixed(2),
        };
      default:
        return { monthly: "0", yearly: "0" };
    }
  };

  // Update other allowance calculation
  useEffect(() => {
    if (!templateData.totalAmount) return;

    const totalAmountNum =
      parseFloat(templateData.totalAmount.toString().replace(/,/g, "")) || 0;
    const sum = tableData
      .filter((row) => row.payHead !== "DEDUCTIONS")
      .reduce((total, row) => total + parseFloat(row.monthly), 0);

    const newOtherAllowance = totalAmountNum - sum;
    setTemplateData((prev) => ({ ...prev, otherAllowance: newOtherAllowance }));
  }, [tableData, templateData.totalAmount]);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return !value ? "Name is required" : "";
      case "period":
        return !value ? "Period is required" : "";
      case "totalAmount":
        return !value ? "Total amount is required" : "";
      case "basic":
        return !value ? "Basic is required" : "";
      default:
        return "";
    }
  };

  const handleInputChange = (name, value) => {
    setTemplateData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleAddBasic = (selectedBasic) => {
    if (!selectedBasic) {
      setTableData(tableData.filter((row) => row.payHead !== "BASIC"));
      setTemplateData((prev) => ({ ...prev, basic: null }));
      return;
    }

    if (!templateData.totalAmount) {
      setErrors((prev) => ({
        ...prev,
        totalAmount: "Enter total amount first",
      }));
      return;
    }

    const calculatedBasic = calculateAmount(
      selectedBasic.calculationType,
      selectedBasic.amountOrPerc,
      templateData.totalAmount
    );

    const basicRow = {
      name: selectedBasic.name,
      payHead: "BASIC",
      type: selectedBasic,
      calculationType: selectedBasic.calculationType,
      amount: `${selectedBasic.amountOrPerc}${
        selectedBasic.calculationType === "FIXED" ? " AED" : "%"
      }`,
      monthly: calculatedBasic.monthly,
      yearly: calculatedBasic.yearly,
      _id: selectedBasic._id,
    };

    const newTableData = [
      ...tableData.filter((row) => row.payHead !== "BASIC"),
      basicRow,
    ];

    const totalMonthly = newTableData
      .filter((row) => row.payHead !== "DEDUCTIONS")
      .reduce((sum, row) => sum + parseFloat(row.monthly), 0);

    const totalAmount = parseFloat(
      templateData.totalAmount.toString().replace(/,/g, "")
    );

    if (totalMonthly > totalAmount) {
      alert("Basic amount would exceed the total amount");
      return;
    }

    setTableData(newTableData);
    setTemplateData((prev) => ({ ...prev, basic: selectedBasic }));
    setErrors((prev) => ({ ...prev, basic: "" }));
  };

  const handleAddItem = (type) => {
    if (!templateData.totalAmount) {
      setErrors((prev) => ({
        ...prev,
        totalAmount: "Enter total amount first",
      }));
      return;
    }

    const currentItem = templateData[type];
    const basicRow = tableData.find((row) => row.payHead === "BASIC");

    const calculatedAmount = calculateAmount(
      currentItem.calculationType,
      currentItem.amountOrPerc,
      templateData.totalAmount,
      basicRow?.monthly
    );

    const newRow = {
      name: currentItem.name,
      payHead: type.toUpperCase(),
      type: currentItem,
      calculationType: currentItem.calculationType,
      amount: `${currentItem.amountOrPerc}${
        currentItem.calculationType === "FIXED" ? " AED" : "%"
      }`,
      monthly: calculatedAmount.monthly,
      yearly: calculatedAmount.yearly,
      _id: currentItem._id,
    };

    if (type !== "deductions") {
      const potentialNewTotal =
        tableData
          .filter((row) => row.payHead !== "DEDUCTIONS")
          .reduce((sum, row) => sum + parseFloat(row.monthly), 0) +
        parseFloat(calculatedAmount.monthly);

      const totalAmount = parseFloat(
        templateData.totalAmount.toString().replace(/,/g, "")
      );

      if (potentialNewTotal > totalAmount) {
        alert("Adding this amount would exceed the total amount");
        return;
      }
    }

    setTableData((prev) => [...prev, newRow]);
    setTemplateData((prev) => ({ ...prev, [type]: null }));
  };

  const handleUpdateItem = () => {
    if (!editingItem) return;

    const type = editingItem.type;
    const currentItem = templateData[type];
    const basicRow = tableData.find((row) => row.payHead === "BASIC");

    const calculatedAmount = calculateAmount(
      currentItem.calculationType,
      currentItem.amountOrPerc,
      templateData.totalAmount,
      basicRow?.monthly
    );

    const newTableData = tableData.map((row, index) =>
      index === editingItem.index
        ? {
            name: currentItem.name,
            payHead: type.toUpperCase(),
            type: currentItem,
            calculationType: currentItem.calculationType,
            amount: `${currentItem.amountOrPerc}${
              currentItem.calculationType === "FIXED" ? " AED" : "%"
            }`,
            monthly: calculatedAmount.monthly,
            yearly: calculatedAmount.yearly,
            _id: currentItem._id,
          }
        : row
    );

    if (type !== "deductions") {
      const totalMonthly = newTableData
        .filter((row) => row.payHead !== "DEDUCTIONS")
        .reduce((sum, row) => sum + parseFloat(row.monthly), 0);

      const totalAmount = parseFloat(
        templateData.totalAmount.toString().replace(/,/g, "")
      );

      if (totalMonthly > totalAmount) {
        alert("Updating this amount would exceed the total amount");
        return;
      }
    }

    setTableData(newTableData);
    setEditingItem(null);
    setTemplateData((prev) => ({ ...prev, [type]: null }));
  };

  const handleEditItem = (index) => {
    const itemToEdit = tableData[index];
    const payHeadType = itemToEdit.payHead.toLowerCase();

    let selectedPayHead;
    switch (payHeadType) {
      case "allowance":
        selectedPayHead = payHeadOptions.allowances.find(
          (ph) => ph._id === itemToEdit.type._id
        );
        setTemplateData((prev) => ({
          ...prev,
          allowances: selectedPayHead || itemToEdit.type,
        }));
        setEditingItem({ index, type: "allowances" });
        break;
      case "deduction":
        selectedPayHead = payHeadOptions.deductions.find(
          (ph) => ph._id === itemToEdit.type._id
        );
        setTemplateData((prev) => ({
          ...prev,
          deductions: selectedPayHead || itemToEdit.type,
        }));
        setEditingItem({ index, type: "deductions" });
        break;
      default:
        break;
    }
  };

  const totalSummary = useMemo(() => {
    return tableData.reduce(
      (acc, row) => ({
        monthly: (parseFloat(acc.monthly) + parseFloat(row.monthly)).toFixed(2),
        yearly: (parseFloat(acc.yearly) + parseFloat(row.yearly)).toFixed(2),
      }),
      { monthly: "0", yearly: "0" }
    );
  }, [tableData]);

  useEffect(() => {
    setLoading(true);
    listPayHeadApi({
      body: { page: 0, limit: 0, search: "", category: "" },
      setPayHead: (data) => {
        setPayHeads(data);
        setPayHeadOptions({
          basic: data.filter((item) => item.category === "BASIC"),
          allowances: data.filter((item) => item.category === "ALLOWANCE"),
          deductions: data.filter((item) => item.category === "DEDUCTION"),
        });
        setLoading(false);
      },
    });
  }, []);

  // Updated period change effect
  useEffect(() => {
    if (
      tableData.length > 0 &&
      (templateData.period || templateData.totalAmount)
    ) {
      const basicRow = tableData.find((r) => r.payHead === "BASIC");
      let updatedBasicRow = null;

      // First recalculate basic if it exists
      if (basicRow) {
        const basicCalc = calculateAmount(
          basicRow.type.calculationType,
          basicRow.type.amountOrPerc,
          templateData.totalAmount
        );
        updatedBasicRow = {
          ...basicRow,
          monthly: basicCalc.monthly,
          yearly: basicCalc.yearly,
        };
      }

      // Then recalculate all other rows using the new basic value
      const recalculatedTableData = tableData.map((row) => {
        if (row.payHead === "BASIC") {
          return updatedBasicRow || row;
        }

        const calculatedAmount = calculateAmount(
          row.type.calculationType,
          row.type.amountOrPerc,
          templateData.totalAmount,
          updatedBasicRow?.monthly || null
        );

        return {
          ...row,
          monthly: calculatedAmount.monthly,
          yearly: calculatedAmount.yearly,
        };
      });

      setTableData(recalculatedTableData);
    }
  }, [templateData.period, templateData.totalAmount]);

  // useEffect(() => {
  //   if (
  //     tableData.length > 0 &&
  //     (templateData.period || templateData.totalAmount)
  //   ) {
  //     const recalculatedTableData = tableData.map((row) => {
  //       const calculatedAmount = calculateAmount(
  //         row.type.calculationType,
  //         row.type.amountOrPerc,
  //         templateData.totalAmount,
  //         row.payHead === "BASIC"
  //           ? null
  //           : tableData.find((r) => r.payHead === "BASIC")?.monthly
  //       );
  //       return {
  //         ...row,
  //         monthly: calculatedAmount.monthly,
  //         yearly: calculatedAmount.yearly,
  //       };
  //     });
  //     setTableData(recalculatedTableData);
  //   }
  // }, [templateData.period, templateData.totalAmount]);

  const navigateTo = () => {
    navigate("/userdashboard/payroll/configuration/PayTemplatePayroll");
  };

  const handleSubmit = () => {
    const newErrors = {
      name: validateField("name", templateData.name),
      period: validateField("period", templateData.period),
      totalAmount: validateField("totalAmount", templateData.totalAmount),
      basic: validateField("basic", templateData.basic),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    if (templateData?.otherAllowance < 0) {
      alert("Sum of basic and allowances exceeds the total amount!");
      return;
    }

    if (tableData.length === 0) {
      alert("Table data is empty");
      return;
    }

    const payload = {
      tempName: templateData?.name,
      period: templateData?.period,
      totalCtc: templateData?.totalAmount,
      basic: templateData?.basic?._id,
      payHeads: tableData
        .filter((row) => row.payHead !== "BASIC")
        .map((row) => ({
          alWncId: row.type._id,
        })),
      otherAllowance: templateData?.otherAllowance,
    };

    if (singleViewId) {
      payload._id = singleViewId;
    }

    createOrUpdatePayTemplateAPI({
      body: payload,
      actionType: singleViewId ? ACTION_TYPE.UPDATE : ACTION_TYPE.ADD,
      setIsEdit,
      setLoading,
      setSnackbar,
      navigateTo,
    });
  };

  const handleEdit = () => {
    setIsEdit(!isEdit);
  };

  useEffect(() => {
    if (!singleViewData) {
      if (singleViewId) {
        fetchPayTemplate({
          body: {
            _id: singleViewId,
          },
          setLoading,
          viewType: VIEW_TYPE.SINGLE,
        });
      }
    } else {
      setIsEdit(true);

      // Find the basic payhead
      const basicPayHead = payHeads?.find(
        (ph) => ph._id === singleViewData?.basic
      );

      const structured = {
        name: singleViewData?.tempName,
        period: singleViewData?.period,
        totalAmount: singleViewData?.totalCtc,
        basic: basicPayHead,
        otherAllowance: singleViewData?.otherAllowance,
      };

      // Initialize table data array
      const newTableData = [];

      // Add basic only if it exists and isn't already in the table
      if (
        basicPayHead &&
        !newTableData.some((row) => row.payHead === "BASIC")
      ) {
        const basicCalc = calculateAmount(
          basicPayHead.calculationType,
          basicPayHead.amountOrPerc,
          singleViewData.totalCtc
        );

        newTableData.push({
          name: basicPayHead.name,
          payHead: "BASIC",
          type: basicPayHead,
          calculationType: basicPayHead.calculationType,
          amount: `${basicPayHead.amountOrPerc}${
            basicPayHead.calculationType === "FIXED" ? " AED" : "%"
          }`,
          monthly: basicCalc.monthly,
          yearly: basicCalc.yearly,
          _id: basicPayHead._id,
        });
      }

      // Filter out any basic payheads from the payHeads array before processing
      const nonBasicPayHeads = singleViewData?.payHeads?.filter((ph) => {
        const payHead = payHeads?.find((p) => p._id === ph.alWncId);
        return payHead && payHead.category !== "BASIC";
      });

      // Add other payheads
      nonBasicPayHeads?.forEach((ph) => {
        const payHead = payHeads?.find((p) => p._id === ph.alWncId);
        if (payHead) {
          const calculated = calculateAmount(
            payHead.calculationType,
            payHead.amountOrPerc,
            singleViewData.totalCtc,
            newTableData.find((row) => row.payHead === "BASIC")?.monthly
          );

          newTableData.push({
            name: payHead.name,
            payHead: payHead.category,
            type: payHead,
            calculationType: payHead.calculationType,
            amount: `${payHead.amountOrPerc}${
              payHead.calculationType === "FIXED" ? " AED" : "%"
            }`,
            monthly: calculated.monthly,
            yearly: calculated.yearly,
            _id: payHead._id,
          });
        }
      });

      setTableData(newTableData);
      setTemplateData(structured);
    }
  }, [singleViewData, payHeads]);
  return (
    <Pagelayout
      pageName="Create Pay Template"
      headerLayout={2}
      handleBack={navigateTo}
      newData={!isEdit}
      isLoading={loading}
      handleSave={handleSubmit}
      handleEdit={handleEdit}
      snackbar={snackbar}
      setSnackbar={setSnackbar}
      status={singleViewId?"done":"new"}
    >
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
            label="NAME"
            value={templateData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={isEdit}
          />
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            size="small"
            disabled={isEdit}
            options={["MONTHLY", "YEARLY"]}
            value={templateData.period}
            onChange={(_, value) => handleInputChange("period", value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="PERIOD"
                error={!!errors.period}
                helperText={errors.period}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            type="number"
            size="small"
            InputLabelProps={{ shrink: true }}
            disabled={isEdit}
            fullWidth
            label="TOTAL AMOUNT"
            value={templateData.totalAmount}
            onChange={(e) =>
              handleInputChange("totalAmount", Number(e.target.value))
            }
            error={!!errors.totalAmount}
            helperText={errors.totalAmount}
            // InputProps={{
            //   endAdornment: <Typography variant="caption">AED</Typography>,
            // }}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            size="small"
            disabled={isEdit}
            options={payHeadOptions.basic || []}
            value={templateData.basic || null}
            getOptionLabel={(option) => option?.name || ""}
            getOptionKey={(option) => option?._id || option?.name}
            onChange={(_, value) => handleAddBasic(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="BASIC"
                error={!!errors.basic}
                helperText={errors.basic}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Autocomplete
                size="small"
                disabled={isEdit}
                options={payHeadOptions.allowances || []}
                value={templateData.allowances}
                getOptionLabel={(option) => option?.name || ""}
                getOptionKey={(option) => option?._id || option?.name}
                onChange={(_, value) =>
                  setTemplateData((prev) => ({ ...prev, allowances: value }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="ALLOWANCE"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() =>
                  editingItem?.type === "allowances"
                    ? handleUpdateItem()
                    : handleAddItem("allowances")
                }
                disabled={
                  !templateData.allowances || !templateData.allowances.name
                }
              >
                {editingItem?.type === "allowances" ? "Update" : "Add"}{" "}
                Allowance
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Autocomplete
                size="small"
                disabled={isEdit}
                options={payHeadOptions.deductions || []}
                value={templateData.deductions}
                getOptionLabel={(option) => option?.name || ""}
                getOptionKey={(option) => option?._id || option?.name}
                onChange={(_, value) =>
                  setTemplateData((prev) => ({ ...prev, deductions: value }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="DEDUCTION"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() =>
                  editingItem?.type === "deductions"
                    ? handleUpdateItem()
                    : handleAddItem("deductions")
                }
                disabled={
                  !templateData.deductions || !templateData.deductions.name
                }
              >
                {editingItem?.type === "deductions" ? "Update" : "Add"}{" "}
                Deduction
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" color="primary">
            Other Allowance: {templateData?.otherAllowance?.toFixed(2)} 
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 400, overflow: "auto" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Pay Head</TableCell>
                  <TableCell>Calculation Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Monthly</TableCell>
                  <TableCell>Yearly</TableCell>
                  {!isEdit && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow key={index || row._id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.payHead}</TableCell>
                    <TableCell>{row.calculationType}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{parseFloat(row.monthly).toFixed(2)}</TableCell>
                    <TableCell>{parseFloat(row.yearly).toFixed(2)}</TableCell>
                    {!isEdit && (
                      <TableCell>
                        {row.payHead !== "BASIC" && (
                          <IconButton
                            onClick={() => handleEditItem(index)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() => {
                            const newTableData = tableData.filter(
                              (_, i) => i !== index
                            );
                            setTableData(newTableData);
                            row.payHead === "BASIC" &&
                              setTemplateData((prev) => ({
                                ...prev,
                                basic: null,
                              }));
                            row.payHead === "BASIC" && setTableData([]);
                          }}
                          size="small"
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "white",
                    fontWeight: "bold",
                  }}
                >
                  <TableCell colSpan={4} align="right">
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell>
                    <strong>
                      {parseFloat(totalSummary.monthly).toFixed(2)}
                    </strong>
                  </TableCell>
                  <TableCell>
                    <strong>
                      {parseFloat(totalSummary.yearly).toFixed(2)}
                    </strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Pagelayout>
  );
};

export default CreatePayTemplate;
